import React from 'react';

import CodeIcon from '@mui/icons-material/Code';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';

import CustomButton from 'components/button/CustomButton';
import TextArea from 'components/form/TextArea';
import { WorkspaceFileSelectDialog } from 'components/training';
import { MSG_REQUIRED } from 'constant/ValidationMessages';
import { getCodeExecutionResult } from 'stores/CodeExecuteStoreAdapter';
import { CreateQuestionParams, createQuestion } from 'stores/TrainingAdminStoreAdapter';
import { WorkSpaceNode, getFileDetail, getWorkSpace } from 'stores/WorkSpaceStoreAdapter';
import { errorDefault, successDefault, toErrorDetail } from 'utils/Toastify';

import AnswerCodesEditor, { EditableAnswerCode } from './AnswerCodesEditor';
import { toAnswerCodeInputs, validateAnswerCodes } from './answerCodeUtils';
import { buildFileRefResolver, parseQuestionKnz } from './questionKnzSync';

type Props = {
  onCreated: () => void;
  onCancel: () => void;
};

const TITLE_MAX = 128;
const CONTENT_MAX = 100000;

/** knz プレフィルのプレビュー（フォーム入力と同じ見た目で表示するための解決済みモデル）。 */
type KnzPreviewExpected = {
  sourceFileId: string;
  sourceSeq: number;
  argumentsPreview: string | null;
  stdoutPreview: string | null;
};
type KnzPreviewAnswerCode = {
  fileId: string;
  filePath: string;
  extension: string;
  expected: KnzPreviewExpected[];
};
type KnzPreview = {
  title: string;
  answerCodes: KnzPreviewAnswerCode[];
  /** 送信用の構造化データ（AnswerCodeInput 相当） */
  inputs: CreateQuestionParams['answerCodes'];
  /** エラー（解決失敗・実行結果取得失敗・必須欠落）。空でなければ登録不可 */
  errors: string[];
};

/**
 * 設問登録フォーム（2タブ）。
 * - knz指定（KNZ・既定）: workspace で作成済みの knz を指定 → フロントがパース・解決してプレフィル → 確認・登録
 * - フォーム入力（FORM）: title＋md本文＋正解コード/期待値フォーム
 */
const QuestionCreateForm: React.FC<Props> = ({ onCreated, onCancel }) => {
  // 既定は knz 指定
  const [tab, setTab] = React.useState<'FORM' | 'KNZ'>('KNZ');
  const [submitting, setSubmitting] = React.useState(false);

  // --- FORM タブ ---
  const [title, setTitle] = React.useState('');
  const [content, setContent] = React.useState('');
  const [answerCodes, setAnswerCodes] = React.useState<EditableAnswerCode[]>([]);

  // --- KNZ タブ ---
  const [knzDialogOpen, setKnzDialogOpen] = React.useState(false);
  const [knzFile, setKnzFile] = React.useState<{ fileId: string; name: string } | null>(null);
  const [preview, setPreview] = React.useState<KnzPreview | null>(null);
  const [previewLoading, setPreviewLoading] = React.useState(false);

  /** knz を解決してプレビューモデルを構築する（正解コード・実行結果の取得エラーもここで検出）。 */
  const buildPreview = async (detail: string, nodes: WorkSpaceNode[]): Promise<KnzPreview> => {
    const resolver = buildFileRefResolver(nodes);
    const parsed = parseQuestionKnz(detail, resolver);
    const errors = [...parsed.errors];

    // 拡張子・パスの逆引き
    const byId = new Map<string, WorkSpaceNode>();
    nodes.forEach((n) => {
      if (n.type === 'FILE' && n.fileId) byId.set(n.fileId, n);
    });
    const extractExt = (name: string): string => {
      const i = name.lastIndexOf('.');
      return i === -1 ? '' : name.slice(i + 1).toLowerCase();
    };

    const answerCodes: KnzPreviewAnswerCode[] = [];
    for (const ac of parsed.answerCodes) {
      const node = ac.fileId ? byId.get(ac.fileId) : undefined;
      const extension = node ? extractExt(node.name) : '';
      const filePath = node?.path ?? '';
      const expected: KnzPreviewExpected[] = [];
      for (const ex of ac.expectedList) {
        let argumentsPreview: string | null = null;
        let stdoutPreview: string | null = null;
        // 実行結果（arguments/stdout）を取得。取得できなければエラーにする（登録不可）。
        if (ex.sourceFileId && ex.sourceSeq >= 0) {
          try {
            // eslint-disable-next-line no-await-in-loop
            const result = await getCodeExecutionResult(ex.sourceFileId, ex.sourceSeq);
            argumentsPreview = result.arguments;
            stdoutPreview = result.stdout;
          } catch {
            errors.push(
              `期待値の実行結果が取得できません（${filePath || ex.sourceFileId} / 実行 #${ex.sourceSeq}）`,
            );
          }
        }
        expected.push({
          sourceFileId: ex.sourceFileId,
          sourceSeq: ex.sourceSeq,
          argumentsPreview,
          stdoutPreview,
        });
      }
      answerCodes.push({ fileId: ac.fileId, filePath, extension, expected });
    }

    return {
      title: parsed.title,
      answerCodes,
      inputs: parsed.answerCodes,
      errors,
    };
  };

  const handleSelectKnz = async (file: {
    fileId: string;
    name: string;
    extension: string;
    path: string;
  }) => {
    setKnzDialogOpen(false);
    setKnzFile({ fileId: file.fileId, name: file.name });
    setPreview(null);
    setPreviewLoading(true);
    try {
      const [detail, nodes] = await Promise.all([getFileDetail(file.fileId), getWorkSpace()]);
      const result = await buildPreview(detail.detail ?? '', nodes);
      setPreview(result);
    } catch (e) {
      errorDefault('knzファイルの読み込みに失敗しました', toErrorDetail(e));
    } finally {
      setPreviewLoading(false);
    }
  };

  const submitForm = async () => {
    if (title.trim() === '') {
      errorDefault(MSG_REQUIRED('設問タイトル'));
      return;
    }
    if (content.trim() === '') {
      errorDefault(MSG_REQUIRED('設問本文（Markdown）'));
      return;
    }
    const validationError = validateAnswerCodes(answerCodes);
    if (validationError) {
      errorDefault(validationError);
      return;
    }
    const params: CreateQuestionParams = {
      title: title.trim(),
      sourceType: 'FORM',
      content,
      answerCodes: toAnswerCodeInputs(answerCodes),
    };
    await doCreate(params);
  };

  const submitKnz = async () => {
    if (!knzFile || !preview) return;
    if (preview.errors.length > 0 || preview.title.trim() === '') return;
    const params: CreateQuestionParams = {
      title: preview.title.trim(),
      sourceType: 'KNZ',
      sourceFileId: knzFile.fileId,
      answerCodes: preview.inputs,
    };
    await doCreate(params);
  };

  const doCreate = async (params: CreateQuestionParams) => {
    setSubmitting(true);
    try {
      await createQuestion(params);
      successDefault('設問を登録しました');
      onCreated();
    } catch (e) {
      errorDefault('設問の登録に失敗しました', toErrorDetail(e));
    } finally {
      setSubmitting(false);
    }
  };

  // KNZ タブの登録可否: プレビュー済み・エラーなし・title あり
  const knzCanSubmit =
    !!knzFile &&
    !!preview &&
    preview.errors.length === 0 &&
    preview.title.trim() !== '' &&
    !previewLoading;

  return (
    <Box>
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab value="KNZ" label="knz指定" sx={{ textTransform: 'none', fontWeight: 600 }} />
        <Tab value="FORM" label="フォーム入力" sx={{ textTransform: 'none', fontWeight: 600 }} />
      </Tabs>

      {tab === 'KNZ' && (
        <Stack spacing={2}>
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            workspace で作成済みの knz ファイルを指定します。knz の作成・編集は workspace
            のエディタで行います。
          </Alert>
          <Box>
            <CustomButton
              label={knzFile ? `knzファイル: ${knzFile.name}` : 'knzファイルを選択'}
              buttonVariant="secondary"
              icon={<DescriptionOutlinedIcon />}
              onClick={() => setKnzDialogOpen(true)}
            />
          </Box>

          {previewLoading && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 2 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                knz を読み込み中...
              </Typography>
            </Stack>
          )}

          {preview && !previewLoading && (
            <Stack spacing={2}>
              {preview.errors.length > 0 && (
                <Alert severity="error" sx={{ borderRadius: 2, whiteSpace: 'pre-wrap' }}>
                  {preview.errors.join('\n')}
                </Alert>
              )}

              {/* タイトル（フォームと同じ TextField 見た目・参照専用） */}
              <TextField
                label="設問タイトル"
                size="small"
                value={preview.title}
                slotProps={{ input: { readOnly: true } }}
                error={preview.title.trim() === ''}
                helperText={preview.title.trim() === '' ? 'knz に title がありません' : ' '}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />

              <Divider />

              {/* 正解コード・期待値（フォームと同じカード見た目・参照専用） */}
              <KnzAnswerCodesPreview answerCodes={preview.answerCodes} />
            </Stack>
          )}
        </Stack>
      )}

      {tab === 'FORM' && (
        <Stack spacing={2}>
          <TextField
            label="設問タイトル"
            required
            size="small"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            slotProps={{ htmlInput: { maxLength: TITLE_MAX } }}
            helperText={`${title.length}/${TITLE_MAX}`}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          <TextArea
            label="設問本文（Markdown）"
            required
            value={content}
            onChange={setContent}
            maxLength={CONTENT_MAX}
            rows={8}
          />
          <Divider />
          <AnswerCodesEditor answerCodes={answerCodes} onChange={setAnswerCodes} />
        </Stack>
      )}

      <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 3 }}>
        <CustomButton label="キャンセル" buttonVariant="secondary" onClick={onCancel} />
        <CustomButton
          label="登録"
          buttonVariant="primary"
          onClick={tab === 'FORM' ? submitForm : submitKnz}
          disabled={submitting || (tab === 'KNZ' && !knzCanSubmit)}
        />
      </Stack>

      <WorkspaceFileSelectDialog
        open={knzDialogOpen}
        onClose={() => setKnzDialogOpen(false)}
        onSelect={handleSelectKnz}
        title="設問用の knz ファイルを選択"
        filter={['knz']}
      />
    </Box>
  );
};

/**
 * knz プレフィルの正解コード・期待値を、フォーム入力（AnswerCodesEditor）と同じ見た目で
 * 参照専用表示する。
 */
const KnzAnswerCodesPreview: React.FC<{ answerCodes: KnzPreviewAnswerCode[] }> = ({
  answerCodes,
}) => (
  <Box>
    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
      正解コード・期待値
    </Typography>
    {answerCodes.length === 0 ? (
      <Box
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          borderStyle: 'dashed',
          p: 3,
          textAlign: 'center',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          knz に正解コード（AnswerCode）がありません
        </Typography>
      </Box>
    ) : (
      <Stack spacing={1.5}>
        {answerCodes.map((ac, aIdx) => (
          <Box
            key={aIdx}
            sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}
          >
            {/* 正解コードヘッダー */}
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ px: 1.5, py: 1, bgcolor: 'action.hover' }}
            >
              <CodeIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              {ac.extension ? (
                <Chip size="small" label={ac.extension} sx={{ borderRadius: 1, fontWeight: 600 }} />
              ) : (
                <Chip size="small" color="error" label="言語不明" sx={{ borderRadius: 1 }} />
              )}
              <Typography variant="body2" sx={{ flex: 1 }} noWrap>
                {ac.filePath || ac.fileId || '(未解決)'}
              </Typography>
            </Stack>

            {/* 期待値リスト */}
            <Box sx={{ p: 1.5 }}>
              {ac.expected.length === 0 ? (
                <Typography variant="caption" color="error.main">
                  期待値（Expected）がありません
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {ac.expected.map((ex, eIdx) => (
                    <Box
                      key={eIdx}
                      sx={{
                        borderLeft: 3,
                        borderColor: 'primary.main',
                        borderRadius: 1,
                        bgcolor: 'action.hover',
                        px: 1.5,
                        py: 1,
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Chip
                          size="small"
                          label={`期待値 ${eIdx + 1}`}
                          variant="outlined"
                          sx={{ borderRadius: 1 }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                          由来: 実行 #{ex.sourceSeq}
                        </Typography>
                      </Stack>
                      <Box sx={{ mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          引数: {ex.argumentsPreview ?? '(なし)'}
                        </Typography>
                        <Box
                          component="pre"
                          sx={{
                            m: 0,
                            mt: 0.25,
                            p: 0.75,
                            borderRadius: 1,
                            bgcolor: 'background.paper',
                            border: 1,
                            borderColor: 'divider',
                            fontSize: 11,
                            maxHeight: 100,
                            overflow: 'auto',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-all',
                          }}
                        >
                          {ex.stdoutPreview ?? '(出力なし)'}
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
          </Box>
        ))}
      </Stack>
    )}
  </Box>
);

export default QuestionCreateForm;
